import { useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useClient, type OrpcClient } from './useClient'
import { logError } from '@/utils/errorUtils'

// Per-page patient read of a fax (PD-1988): the OCR read, the page groups per patient and
// where a split filing stands. Rendered above the single-patient form, which stays the
// fallback while this is pending, failed or absent.
export type FaxPatients = Awaited<ReturnType<OrpcClient['getIncomingFaxPatients']>>

export const faxPatientsQueryKey = (taskId: string) => ['fax-patients', taskId] as const

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 5 * 60_000 // OCR of a long fax is slower than the single-page read

export function useFaxPatients(taskId: string) {
  const client = useClient()
  const pendingStartRef = useRef<number | null>(null)

  return useQuery({
    queryKey: faxPatientsQueryKey(taskId),
    // A failed read throws rather than resolving empty: a failed refetch then keeps the data it
    // had, so an open split form is not swapped out from under staff. A first read that fails
    // leaves data undefined, and callers keep today's form.
    queryFn: async (): Promise<FaxPatients> => {
      try {
        return await client.getIncomingFaxPatients({ id: taskId })
      } catch (err) {
        logError('fax', 'Failed to read the patients on this fax', err)
        throw err
      }
    },
    refetchInterval: (query) => {
      if (query.state.data?.status !== 'pending') {
        pendingStartRef.current = null
        return false
      }
      pendingStartRef.current ??= Date.now()
      return Date.now() - pendingStartRef.current > POLL_TIMEOUT_MS ? false : POLL_INTERVAL_MS
    },
  })
}
